import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  FaPaperPlane,
  FaComments,
  FaChevronUp,
  FaChevronDown,
  FaEyeSlash
} from 'react-icons/fa';
import Select from 'react-select';
import { Modal, Button, Alert } from 'react-bootstrap';
import {
  getMessages,
  sendMessage,
  hideMessage,
  getDepartements,
  getUsersSimple,
  getOuvriers
} from '../services/api';
import socket, { connectSocket, disconnectSocket } from '../socket_';
import './Chatbox.css';

const Chatbox = ({ user }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [urgence, setUrgence] = useState('normal');
  const [departements, setDepartements] = useState([]);
  const [selectedDepartements, setSelectedDepartements] = useState([
    { value: null, label: '🌍 TOUT' }
  ]);
  const [users, setUsers] = useState([]);
  const [selectedDmUser, setSelectedDmUser] = useState(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [ouvrierMap, setOuvrierMap] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const [flashRed, setFlashRed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sendError, setSendError] = useState(null);

  const messagesEndRef = useRef(null);
  const notificationSound = useRef(null);

  const userId = user?.id || 0;

  const userDepartements = useMemo(() => {
    return Array.isArray(user?.departements)
      ? user.departements.map(Number)
      : JSON.parse(localStorage.getItem('user_departements') || '[]').map(Number);
  }, [user]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const appendMessageIfMissing = (message) => {
    setMessages((prev) => {
      const alreadyExists = prev.some((msg) => msg.id === message.id);
      if (alreadyExists) return prev;
      return [...prev, message];
    });
  };

  const isMessageVisibleForUser = (msg) => {
    const depId = msg?.departement_id;

    const isGlobal = depId === null || depId === undefined;
    const isAuthorized = depId !== null && depId !== undefined
      ? userDepartements.includes(Number(depId))
      : false;
    const isSender = msg?.sender?.id === userId || msg?.sender_id === userId;
    const isDm = Number(msg?.id_dm_user) === userId;

    return isGlobal || isAuthorized || isSender || isDm;
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const res = await getMessages();
      const all = Array.isArray(res.data) ? res.data : [];
      setMessages(all.filter(isMessageVisibleForUser));
      setTimeout(() => scrollToBottom(), 100);
    } catch (error) {
      console.error('❌ Erreur chargement messages :', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDepartements = async () => {
    try {
      const res = await getDepartements();
      const all = [{ id: null, name: '🌍 TOUT' }, ...(res.data || [])];
      setDepartements(all);
    } catch (error) {
      console.error('❌ Erreur chargement départements :', error);
    }
  };

  const loadOuvriers = async () => {
    try {
      const res = await getOuvriers();
      const map = {};
      (res.data || []).forEach((o) => {
        map[o.id] = o;
      });
      setOuvrierMap(map);
    } catch (error) {
      console.error('❌ Erreur chargement ouvriers :', error);
    }
  };

  const loadUsers = async () => {
    try {
      const res = await getUsersSimple();
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('❌ Erreur chargement utilisateurs :', error);
    }
  };

  useEffect(() => {
    if (!user?.id) return;

    loadMessages();
    loadDepartements();
    loadOuvriers();
    loadUsers();

    connectSocket();

    const handleNewMessage = (data) => {
      if (!isMessageVisibleForUser(data)) {
        return;
      }

      appendMessageIfMissing(data);
      setTimeout(() => scrollToBottom(), 100);

      if (!isOpen) {
        setHasNewMsg(true);
      }

      if (data.urgence === 'medium' || data.urgence === 'high') {
        if (notificationSound.current) {
          notificationSound.current.play().catch(() => {});
        }

        if (navigator.vibrate) {
          navigator.vibrate(200);
        }

        if (data.urgence === 'high') {
          setFlashRed(true);
          setIsOpen(true);
          setTimeout(() => setFlashRed(false), 3000);
        }
      }
    };

    const handleHideMessage = (data) => {
      setMessages((prev) => prev.filter((msg) => msg.id !== data.id));
    };

    socket.on('newMessage', handleNewMessage);
    socket.on('hideMessage', handleHideMessage);

    return () => {
      socket.off('newMessage', handleNewMessage);
      socket.off('hideMessage', handleHideMessage);
      disconnectSocket();
    };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    const safeMessage = String(newMsg || '').trim();
    const depValues = selectedDepartements.map((dep) => dep.value);

    if (!safeMessage) return;
    if (!depValues.length) return;

    try {
      const messageData = {
        departement_ids: depValues,
        id_dm_user: selectedDmUser?.value || null,
        content: safeMessage,
        urgence
      };

      await sendMessage(messageData);
      setNewMsg('');
      setUrgence('normal');
      setSelectedDmUser(null);
      setSelectedDepartements([{ value: null, label: '🌍 TOUT' }]);
      setShowSendModal(false);
    } catch (error) {
      console.error('❌ Erreur envoi message :', error);
      setSendError(error.response?.data?.error || "Erreur lors de l’envoi du message");
    }
  };

  const handleHideMessage = async (id) => {
    try {
      await hideMessage(id, false);
      setMessages((prev) => prev.filter((msg) => msg.id !== id));
    } catch (error) {
      console.error('❌ Erreur en cachant le message :', error);
    }
  };

  const renderMessageContent = (text) => {
    const ouvrierRegex = /ouvrier:(\d+)/g;
    const raw = String(text || '');
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = ouvrierRegex.exec(raw)) !== null) {
      if (match.index > lastIndex) {
        parts.push(raw.slice(lastIndex, match.index));
      }
      const id = match[1];
      const ouvrier = ouvrierMap[id];
      const label = ouvrier ? ouvrier.name : match[0];
      parts.push(
        <span
          key={`ouvrier-${id}-${match.index}`}
          className="link-ouvrier"
          style={{ fontWeight: 'bold', color: 'green', cursor: 'pointer', textDecoration: 'underline' }}
        >
          👷‍♂️ {label}
        </span>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < raw.length) {
      parts.push(raw.slice(lastIndex));
    }

    return parts;
  };

  const toggleChatbox = () => {
    setIsOpen((prev) => !prev);
    if (hasNewMsg) {
      setHasNewMsg(false);
    }
  };

  return (
    <>
      <audio ref={notificationSound} src="/notif1.wav" preload="auto" />

      <div
        className={`chatbox-container ${!isOpen ? 'collapsed' : ''} ${
          flashRed ? 'flash-red' : ''
        }`}
      >
        <div
          className={`chatbox-header ${hasNewMsg ? 'has-new-message' : ''}`}
          onClick={toggleChatbox}
        >
          <FaComments /> SHEMS CHAT
          <span className="chatbox-toggle-icon">
            {isOpen ? <FaChevronDown /> : <FaChevronUp />}
          </span>
          {hasNewMsg && !isOpen && <span className="chatbox-new-badge">Nouveau</span>}
        </div>

        {isOpen && (
          <>
            <div className="chatbox-messages">
              {loading && (
                <div className="text-center text-muted py-2">Chargement des messages...</div>
              )}

              {messages.map((msg) => (
                <div className="chatbox-message" key={msg.id}>
                  <div
                    style={{
                      fontSize: '0.8rem',
                      color:
                        msg.urgence === 'high'
                          ? '#dc3545'
                          : msg.urgence === 'medium'
                          ? '#fd7e14'
                          : '#6c757d',
                      fontWeight: msg.urgence !== 'normal' ? 'bold' : 'normal'
                    }}
                  >
                    {msg.urgence === 'high' &&
                      `🔴 (${msg.Departement?.name ?? '🌐 TOUT'}) URGENT`}
                    {msg.urgence === 'medium' &&
                      `🟠 (${msg.Departement?.name ?? '🌐 TOUT'}) Moyen`}
                    {msg.urgence === 'normal' && `(${msg.Departement?.name ?? '🌐 TOUT'})`}

                    {(msg.sender?.id === userId || user?.role === 'admin') && (
                      <button
                        onClick={() => {
                          if (
                            window.confirm(
                              '⚠️ Voulez-vous vraiment masquer ce message ?'
                            )
                          ) {
                            handleHideMessage(msg.id);
                          }
                        }}
                        style={{
                          marginLeft: '10px',
                          fontSize: '0.9rem',
                          backgroundColor: 'transparent',
                          color: 'red',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        <FaEyeSlash />
                      </button>
                    )}
                  </div>

                  <strong>{msg.sender?.name ?? 'User'}:</strong>{' '}
                  <span>{renderMessageContent(msg.content)}</span>
                </div>
              ))}

              <div ref={messagesEndRef} />
            </div>

            <div className="chatbox-input">
              <input
                type="text"
                value={newMsg}
                onChange={(e) => setNewMsg(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    setShowSendModal(true);
                  }
                }}
                placeholder="✍️ Écrivez votre message..."
              />
              <button onClick={() => setShowSendModal(true)}>
                <FaPaperPlane />
              </button>
            </div>
          </>
        )}
      </div>

      <Modal show={showSendModal} onHide={() => { setShowSendModal(false); setSendError(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>📤 Préparer votre message</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {sendError && (
            <Alert variant="danger" className="mb-3">
              {sendError}
            </Alert>
          )}
          <div className="mb-3">
            <label>
              <strong>📌 Départements :</strong>
            </label>
            <Select
              isMulti
              options={departements.map((dep) => ({
                value: dep.id,
                label: dep.name
              }))}
              value={selectedDepartements}
              onChange={(value) => setSelectedDepartements(value || [])}
            />
          </div>

          <div className="mb-3">
            <label>
              <strong>👤 Utilisateur direct</strong>
            </label>
            <Select
              options={users.map((u) => ({
                value: u.id,
                label: u.name
              }))}
              value={selectedDmUser}
              onChange={setSelectedDmUser}
              menuPlacement="top"
              isClearable
            />
          </div>

          <div className="mb-3">
            <label>
              <strong>🚨 Urgence :</strong>
            </label>
            <select
              value={urgence}
              onChange={(e) => setUrgence(e.target.value)}
              className="form-control"
            >
              <option value="normal">🟢 Normal</option>
              <option value="medium">🟠 Moyen</option>
              <option value="high">🔴 Urgent</option>
            </select>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSendModal(false)}>
            ❌ Annuler
          </Button>
          <Button variant="primary" onClick={handleSend}>
            ✅ Envoyer
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Chatbox;